import React, { useMemo, useState } from "react";
import { team } from "../data/mock";
import { Linkedin, Mail } from "lucide-react";

// Initials from full name (e.g. "Nigel Drew" -> "ND")
function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

// Stable subtle gradient per name
function gradientFor(name) {
  const palettes = [
    "from-[#1a1a1f] via-[#141418] to-[#0e0e12]",
    "from-[#1e1a10] via-[#15140e] to-[#0e0e0a]",
    "from-[#161a1f] via-[#0f1216] to-[#0a0c0f]",
    "from-[#1a1714] via-[#141210] to-[#0d0c0a]",
    "from-[#15171a] via-[#0f1114] to-[#0a0b0d]"
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % palettes.length;
  return palettes[h];
}

export default function Team() {
  const groups = useMemo(() => ["All", ...Array.from(new Set(team.map((m) => m.group)))], []);
  const [active, setActive] = useState("All");
  const visible = active === "All" ? team : team.filter((m) => m.group === active);

  return (
    <section className="py-24 lg:py-32 bg-[#0a0a0c] border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10 reveal">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Meet the Team</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-2xl">
              People <span className="text-amber-accent">who build it.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/65">
            From the chairman to the yard supervisor, every R&amp;C job is delivered by a team
            who's genuinely invested in the work and the people we serve.
          </p>
        </div>

        {/* Group filter */}
        <div className="flex flex-wrap items-center gap-2 mb-12 reveal">
          {groups.map((g) => (
            <button
              key={g}
              onClick={() => setActive(g)}
              className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-[0.18em] transition-all ${
                active === g
                  ? "bg-[#F5D000] text-black border border-[#F5D000]"
                  : "bg-white/5 text-white/70 border border-white/10 hover:border-white/25 hover:text-white"
              }`}
            >
              {g}
            </button>
          ))}
          <span className="ml-auto text-[11px] uppercase tracking-[0.25em] text-white/45">
            {visible.length} {visible.length === 1 ? "person" : "people"}
          </span>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
          {visible.map((m, i) => (
            <article
              key={m.name}
              className="ri-fade-up group relative rounded-2xl overflow-hidden bg-[#0f0f13] border border-white/8 hover:border-[#F5D000]/45 transition-all duration-500"
              style={{ animationDelay: `${(i % 8) * 40}ms` }}
            >
              {/* Portrait area */}
              <div className={`relative aspect-[5/4] bg-gradient-to-br ${gradientFor(m.name)} overflow-hidden`}>
                {m.image ? (
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-[center_22%] group-hover:scale-[1.04] transition-transform duration-700"
                  />
                ) : (
                  <>
                    {/* Subtle radial glow */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_120%,rgba(245,208,0,0.18),transparent_60%)]" />
                    {/* Grid pattern */}
                    <div
                      className="absolute inset-0 opacity-[0.08]"
                      style={{
                        backgroundImage:
                          "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                        backgroundSize: "28px 28px"
                      }}
                    />
                    {/* Initials monogram */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <div className="absolute -inset-6 rounded-full bg-[#F5D000]/10 blur-2xl group-hover:bg-[#F5D000]/25 transition-colors" />
                        <span className="relative font-heavy text-[88px] leading-none text-white/95 select-none group-hover:text-[#F5D000] transition-colors">
                          {initials(m.name)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                {/* Group tag */}
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 rounded-full bg-black/55 backdrop-blur border border-white/10 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
                    {m.group}
                  </span>
                </div>
                {/* Hover socials */}
                <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 translate-y-[-4px] group-hover:opacity-100 group-hover:translate-y-0 transition-all">
                  <a
                    href="#"
                    aria-label="LinkedIn"
                    onClick={(e) => e.preventDefault()}
                    className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/10 flex items-center justify-center text-white hover:bg-[#F5D000] hover:text-black hover:border-[#F5D000] transition-colors"
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href={`mailto:${m.email || "info@randcwilliams.ltd.uk"}`}
                    aria-label="Email"
                    className="w-8 h-8 rounded-full bg-white/10 backdrop-blur border border-white/10 flex items-center justify-center text-white hover:bg-[#F5D000] hover:text-black hover:border-[#F5D000] transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Caption */}
              <div className="p-5">
                <div className="font-display text-lg font-bold text-white leading-tight">{m.name}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#F5D000] mt-1.5">{m.role}</div>
                <p className="mt-4 text-sm text-white/65 leading-relaxed line-clamp-3">{m.bio}</p>
              </div>

              {/* Accent line */}
              <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#F5D000]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </article>
          ))}
        </div>

        <p className="mt-12 text-xs text-white/40 text-center reveal">
          Names, roles &amp; headshots direct from the R&amp;C team.
        </p>
      </div>
    </section>
  );
}
